import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import {
  PlayCircle,
  GraduationCap,
  HelpCircle,
  Phone,
  LifeBuoy,
  ChevronLeft,
  LogIn,
} from "lucide-react";
import { AyudaGeneralTab } from "./tabs/AyudaGeneralTab";
import { AyudaTutorialesTab } from "./tabs/AyudaTutorialesTab";
import { AyudaFaqTab } from "./tabs/AyudaFaqTab";
import { AyudaContactoTab } from "./tabs/AyudaContactoTab";
import { AyudaAsistenciaTab } from "./tabs/AyudaAsistenciaTab";

type AyudaTab = "general" | "tutoriales" | "faq" | "contacto" | "asistencia";

const TABS: { id: AyudaTab; label: string; icon: React.ReactNode }[] = [
  { id: "general", label: "Tutorial general", icon: <PlayCircle className="h-4 w-4" /> },
  { id: "tutoriales", label: "Tutoriales por módulo", icon: <GraduationCap className="h-4 w-4" /> },
  { id: "faq", label: "Preguntas frecuentes", icon: <HelpCircle className="h-4 w-4" /> },
  { id: "contacto", label: "Contáctanos", icon: <Phone className="h-4 w-4" /> },
  { id: "asistencia", label: "Asistencia", icon: <LifeBuoy className="h-4 w-4" /> },
];

export function CentroAyudaPage() {
  const auth = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = (searchParams.get("tab") as AyudaTab) || "general";
  const [tab, setTab] = useState<AyudaTab>(
    TABS.some((t) => t.id === initial) ? initial : "general",
  );

  const cambiarTab = (next: AyudaTab) => {
    setTab(next);
    const sp = new URLSearchParams(searchParams);
    sp.set("tab", next);
    setSearchParams(sp, { replace: true });
  };

  return (
    <div className="flex min-h-screen flex-col bg-surface text-fg">
      <header className="bg-npmx-700 dark:bg-npmx-950 text-white shadow">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <div className="flex items-center gap-3">
            <div className="rounded-md bg-surface px-3 py-1.5 shadow-sm">
              <img src="/assets/logo-bn.png" alt="NPMX" className="h-7 w-auto" />
            </div>
            <div className="leading-tight">
              <div className="text-base font-semibold">SICEN · Centro de Ayuda</div>
              <div className="text-xs text-white/80">Sistema de Certificaciones · NPMX</div>
            </div>
          </div>
          {auth.isAuthenticated ? (
            <Link
              to="/"
              className="inline-flex items-center gap-1 rounded-md border border-white/30 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-surface/10"
            >
              <ChevronLeft className="h-4 w-4" />
              Volver al portal
            </Link>
          ) : (
            <Link
              to="/login"
              className="inline-flex items-center gap-1 rounded-md bg-surface px-3 py-1.5 text-sm font-medium text-npmx-700 transition hover:bg-surface/90"
            >
              <LogIn className="h-4 w-4" />
              Iniciar sesión
            </Link>
          )}
        </div>
      </header>

      <main className="flex-1">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
          <div className="mb-4">
            <h1 className="text-2xl font-semibold text-fg">Centro de Ayuda SICEN</h1>
            <p className="mt-1 text-sm text-fg-muted">
              Tutoriales, preguntas frecuentes, contacto institucional y asistencia
              personalizada para usuarios del Sistema de Certificaciones de NPMX.
            </p>
          </div>

          <div className="flex flex-col gap-4 lg:flex-row">
            <aside className="lg:w-60 lg:shrink-0">
              <nav className="overflow-hidden rounded-lg border bg-surface shadow-card">
                <ul className="divide-y">
                  {TABS.map((t) => (
                    <li key={t.id}>
                      <button
                        type="button"
                        onClick={() => cambiarTab(t.id)}
                        className={`flex w-full items-center gap-2 px-4 py-3 text-left text-sm transition ${
                          tab === t.id
                            ? "bg-primary-tint font-medium text-primary-strong"
                            : "text-fg-muted hover:bg-surface-2"
                        }`}
                      >
                        <span className={tab === t.id ? "text-primary-strong" : "text-fg-faint"}>
                          {t.icon}
                        </span>
                        <span>{t.label}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            </aside>

            <section className="min-w-0 flex-1">
              {tab === "general" && <AyudaGeneralTab onIrA={cambiarTab} />}
              {tab === "tutoriales" && <AyudaTutorialesTab />}
              {tab === "faq" && <AyudaFaqTab />}
              {tab === "contacto" && <AyudaContactoTab />}
              {tab === "asistencia" && <AyudaAsistenciaTab />}
            </section>
          </div>
        </div>
      </main>

      <footer className="bg-npmx-700 dark:bg-npmx-950 text-white/85">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-3 px-4 py-5 sm:flex-row sm:justify-between sm:px-6">
          <div className="flex items-center gap-3">
            <div className="rounded-md bg-surface px-3 py-1.5 shadow-sm">
              <img src="/assets/logo-bn.png" alt="NPMX" className="h-6 w-auto" />
            </div>
            <div className="text-[11px] leading-tight">
              <div className="font-semibold text-white">SICEN — Sistema de Certificaciones</div>
              <div>Normalización Profesional de México S.C.</div>
            </div>
          </div>
          <div className="text-center text-[11px] sm:text-right">
            © 2026 · Acreditación EMA 130/17 · Autorización IFT/223/UCS/7706/2025
          </div>
        </div>
      </footer>
    </div>
  );
}
